import {MenuConfig, MenuItemConfig} from '@common/menus/menu-config';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {useSettings} from '@ui/settings/use-settings';
import {User} from '@ui/types/user';
import dot from 'dot-object';
import {useMemo} from 'react';
import {useAuth} from '../auth/use-auth';

export function useCustomMenu(menuOrPosition?: string | MenuConfig) {
  const settings = useSettings();
  const {user, hasPermission, hasRole} = useAuth();

  return useMemo(() => {
    if (!menuOrPosition) {
      return null;
    }

    const menu =
      typeof menuOrPosition === 'string'
        ? settings.menus?.find(s => s.positions?.includes(menuOrPosition))
        : menuOrPosition;

    const menuItems: MenuItemConfig[] = [];

    if (menu) {
      for (const item of menu.items) {
        const hasRoles = (item.roles || []).every(a => hasRole(a));
        const hasPermissions = (item.permissions || []).every(a =>
          hasPermission(a),
        );
        const hasSettings =
          !item.settings ||
          Object.entries(item.settings).every(([key, value]) => {
            return dot.pick(key, settings) == value;
          });

        if (
          !item.action ||
          !hasRoles ||
          !hasPermissions ||
          !hasSettings ||
          !subscriptionStatusMatches(item, user)
        ) {
          continue;
        }

        if (user?.id) {
          item.action = item.action.replace(/{currentUser}/g, `${user.id}`);
        }

        item.action = cleanCustomPageUrl(
          item.action,
          settings.base_url,
          getBootstrapData().custom_page_slugs || [],
        );

        menuItems.push(item);
      }
      menu.items = menuItems;
    }

    return menu;
  }, [hasPermission, settings, menuOrPosition, user]);
}

function cleanCustomPageUrl(
  action: string,
  baseUrl: string,
  customPageSlugs: string[],
): string {
  const isAbsolute = /^https?:\/\//i.test(action);

  try {
    const base = new URL(baseUrl);
    const url = new URL(action, base);
    const match = url.pathname.match(/^\/pages\/([^/]+)\/?$/);
    const slug = match?.[1] ? decodeURIComponent(match[1]) : null;

    if (
      url.origin !== base.origin ||
      !slug ||
      !customPageSlugs.includes(slug)
    ) {
      return action;
    }

    url.pathname = `/${slug}`;
    return isAbsolute
      ? url.toString()
      : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return action;
  }
}

function subscriptionStatusMatches(
  item: MenuItemConfig,
  user?: User | null,
): boolean {
  if (!item.subscriptionStatus) {
    return true;
  }
  const hasActiveSubscription = !!user?.subscriptions?.find(sub => sub.active);
  if (item.subscriptionStatus === 'subscribed') {
    return hasActiveSubscription;
  }
  return !hasActiveSubscription;
}
